import React, { useState, useEffect } from 'react';
import API from '../../services';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { setBrowser, setIsLoggedIn } from '../../store/authReducer';
import { useNavigate } from 'react-router-dom';
import { BRAND_NAME } from '../../constants/Branding';
import { RootState } from '../../models/RootState';
import TOKEN_API from '../../services/token';
import { useTranslation } from '../../hooks/useTranslation';
import { getDynamicTextColors } from '../../store/authActions';

interface LoginFormProps {
    userData: {
        mobileNumber: string;
        password: string;
        countryCode: string;
        userId?: string;
    };
    updateUserData: (data: any) => void;
    goToNextStep: () => void;
    startOtpTimer: () => void;
    theme?: 'default' | 'lovable';
    onRegisterClick?: () => void;
    inputBackgroundBox?: string;
    primaryColor?: string;
    tableBorder?: string;
    backgroundTeamInfo?: string;
    backgroundColor?: string;
}

const LoginForm: React.FC<LoginFormProps> = ({
    userData,
    updateUserData,
    goToNextStep,
    startOtpTimer,
    theme = 'default',
    onRegisterClick,
    inputBackgroundBox,
    primaryColor,
    tableBorder,
    backgroundTeamInfo,
    backgroundColor
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    const [isExistingUser, setIsExistingUser] = useState(false);
    const [activeTab, setActiveTab] = useState<'userId' | 'mobile'>('userId');
    const siteConfig = useSelector((state: RootState) => state.userDetails.siteConfig);
    const userDetails = useSelector((state: RootState) => state.userDetails?.userDetails);
    const [demoLoading, setDemoLoading] = useState(false);
    
    // Get dynamic text color classes based on different backgrounds
    const { 
        pageTextColorClass, 
        inputBoxTextColorClass, 
        ionButtonTextColorClass: activeTabTextColorClass, 
        accordionHeaderTextColorClass: inactiveTabTextColorClass 
    } = getDynamicTextColors(userDetails);
    
    // inputLabelTextColorClass uses same as inputBoxTextColorClass (both use --inputbox-color)
    const inputLabelTextColorClass = inputBoxTextColorClass;
    
    // Helper function to get text color for primaryColor
    const getTextColorForPrimaryColor = (bgColor: string | undefined) => {
        if (!bgColor) return undefined;
        
        // Handle hex colors (#RRGGBB or #RGB)
        let rgb: number[] = [];
        if (bgColor.startsWith('#')) {
            const hex = bgColor.slice(1);
            if (hex.length === 3) {
                // Convert #RGB to #RRGGBB
                rgb = [
                    parseInt(hex[0] + hex[0], 16),
                    parseInt(hex[1] + hex[1], 16),
                    parseInt(hex[2] + hex[2], 16)
                ];
            } else if (hex.length === 6) {
                rgb = [
                    parseInt(hex.slice(0, 2), 16),
                    parseInt(hex.slice(2, 4), 16),
                    parseInt(hex.slice(4, 6), 16)
                ];
            }
        } else if (bgColor.startsWith('rgb')) {
            // Handle rgb() or rgba() format
            const matches = bgColor.match(/\d+/g);
            if (matches && matches.length >= 3) {
                rgb = [parseInt(matches[0]), parseInt(matches[1]), parseInt(matches[2])];
            }
        }
        
        if (rgb.length === 3) {
            // Calculate brightness using relative luminance formula
            const brightness = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
            return brightness > 128 ? '#000000' : '#FFFFFF';
        }
        
        // Fallback: use dynamic class based on CSS variable
        return activeTabTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF';
    };
    
    // Helper function to get text color for labels based on backgroundColor from response
    const getLabelTextColor = () => {
        // Use backgroundColor from response/props if available
        if (backgroundColor) {
            const textColor = getTextColorForPrimaryColor(backgroundColor);
            if (textColor) {
                const isDark = textColor === '#000000';
                return { color: textColor, isDark: isDark };
            }
        }
        
        // Fallback: Get the actual page background color from CSS variable
        const pageBgColor = getComputedStyle(document.documentElement).getPropertyValue('--app-bg-color').trim();
        
        if (pageBgColor) {
            const textColor = getTextColorForPrimaryColor(pageBgColor);
            if (textColor) {
                const isDark = textColor === '#000000';
                return { color: textColor, isDark: isDark };
            }
        }
        
        // Final fallback: use dynamic class based on CSS variable
        const fallbackColor = pageTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF';
        const isDark = fallbackColor === '#000000';
        return { color: fallbackColor, isDark: isDark };
    };
    
    // Get the text color for labels
    const labelTextColor = getLabelTextColor();
    const labelTextColorValue = labelTextColor.color;
    const labelTextIsDark = labelTextColor.isDark;
    
    // Forgot Password State
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotPasswordTab, setForgotPasswordTab] = useState<'userId' | 'mobile'>('userId');
    const [forgotPasswordData, setForgotPasswordData] = useState({
        userId: '',
        mobileNumber: '',
        countryCode: '91',
        otp: '',
        newPassword: '',
        confirmPassword: '',
        token: ''
    });
    const [forgotOtpSent, setForgotOtpSent] = useState(false);
    const [forgotOtpLoading, setForgotOtpLoading] = useState(false);
    const [forgotResetLoading, setForgotResetLoading] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [forgotErrors, setForgotErrors] = useState<Record<string, string>>({});
    const [forgotOtpTimer, setForgotOtpTimer] = useState(0);

    // Timer effect for forgot password OTP
    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (forgotOtpTimer > 0) {
            interval = setInterval(() => {
                setForgotOtpTimer(prev => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [forgotOtpTimer]);
    
    // Theme-based classes
    const isLovable = theme === 'lovable';
    // Use backgroundTeamInfo if provided, otherwise use theme defaults
    const tabBgClass = backgroundTeamInfo ? '' : (isLovable ? 'bg-[hsl(275,35%,20%)]' : 'bg-zinc-700');
    const tabRoundedClass = isLovable ? 'rounded-full' : 'rounded-md';
    // Use primaryColor if provided, otherwise use theme defaults
    const tabActiveClass = primaryColor 
        ? '' 
        : (isLovable ? 'bg-[hsl(275,90%,64%)] text-black' : 'bg-blue-600 text-white');
    const tabInactiveClass = isLovable ? 'text-zinc-300 hover:text-white hover:bg-[hsl(275,35%,25%)]' : 'text-zinc-300 hover:text-white hover:bg-zinc-600';
    const inputClass = isLovable 
        ? 'bg-[hsl(275,35%,12%)] text-white border-[hsl(275,90%,64%)] focus:ring-[hsl(275,90%,64%)] placeholder:text-[hsl(275_50%_65%)]' 
        : 'bg-zinc-800 text-white border-zinc-700 focus:ring-blue-500';
    // Use primaryColor if provided, otherwise use theme defaults
    const primaryBtnClass = primaryColor 
        ? '' 
        : (isLovable ? 'bg-[hsl(275,90%,64%)] hover:bg-[hsl(275,90%,70%)] text-black' : 'bg-blue-600 hover:bg-blue-700 text-white');
    // Get dynamic border color for country code based on input background
    const getCountryCodeBorderColor = () => {
        if (tableBorder) return tableBorder;
        // Use CSS variable for inputbox color to determine border
        const bgColor = inputBackgroundBox || getComputedStyle(document.documentElement).getPropertyValue('--inputbox-color').trim();
        if (!bgColor) return undefined;
        // Return border color based on background - use border-color CSS variable if available
        const borderColor = getComputedStyle(document.documentElement).getPropertyValue('--border-color').trim();
        return borderColor || undefined;
    };
    
    const countryCodeBorderStyle = getCountryCodeBorderColor() ? { borderColor: getCountryCodeBorderColor() } : {};
    
    // countryCodeTextColorClass uses same as inputBoxTextColorClass (both use --inputbox-color)
    const countryCodeTextColorClass = inputBoxTextColorClass;
    
    const countryCodeClass = isLovable 
        ? `bg-inputbox-color ${countryCodeTextColorClass === 'dark-text' ? 'text-black' : 'text-[hsl(275_50%_65%)]'}` 
        : `bg-inputbox-color ${countryCodeTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`;
    const linkClass = isLovable ? 'text-[hsl(275,90%,64%)]' : 'text-blue-400';
    
    // iconTextColorClass uses same as inputBoxTextColorClass (both use --inputbox-color)
    const iconTextColorClass = inputBoxTextColorClass;
    
    const iconClass = isLovable 
        ? `${iconTextColorClass === 'dark-text' ? 'text-black' : 'text-[hsl(275_50%_65%)]'}` 
        : `${iconTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`;
    const secondaryBtnClass = isLovable 
        ? 'bg-[hsl(275,50%,45%)] hover:bg-[hsl(275,50%,50%)] text-white' 
        : 'bg-teal-700 hover:bg-teal-800 text-white';
    const timerHighlightClass = isLovable ? 'text-[hsl(275,90%,64%)]' : 'text-cyan-400';
    
    const handleRegisterClick = () => {
        if (onRegisterClick) {
            onRegisterClick();
        } else {
            navigate('/register');
        }
    };
    
    // Check if this is BidExch or hindu777 brand
    const isBidExch = BRAND_NAME === 'BidExch';
    const isHindu777 = BRAND_NAME === 'Hindu777';
    const isSeth247 = BRAND_NAME === 'Seth247';
    const isLotus365 = BRAND_NAME === 'Lotus365';
    const isIframeScore = BRAND_NAME === 'IframeScore';
    const isRajaBet = BRAND_NAME === 'RajaBet';
    const isWinFast99 = BRAND_NAME === 'Winfast99';
    const isKhelo365days = BRAND_NAME === 'Khelo365days';
    const isJKBet = BRAND_NAME === 'JkBetsWin';
    const showUserIdLogin = isBidExch || isHindu777 || isLotus365 || isSeth247 || isIframeScore || isRajaBet || isWinFast99 || isKhelo365days || isJKBet;

    const validateMobileNumber = (value: string) => {
        if (!/^\d{10}$/.test(value)) {
            return 'Please enter a valid 10-digit mobile number';
        }
        return '';
    };

    const validateUserId = (value: string) => {
        if (!value.trim()) {
            return 'User ID is required';
        }
        if (value.length < 3) {
            return 'User ID must be at least 3 characters';
        }
        if (!/^[a-zA-Z0-9]+$/.test(value)) {
            return 'User ID can only contain letters, numbers';
        }
        return '';
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        if (name === 'mobileNumber' && value.length > 10) return;

        updateUserData({ [name]: value });

        // Clear existing error
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }

        // Validate mobile number
        if (name === 'mobileNumber') {
            const error = validateMobileNumber(value);
            if (error) {
                setErrors(prev => ({ ...prev, mobileNumber: error }));
            }
        }

        // Validate userId
        if (name === 'userId') {
            const error = validateUserId(value);
            if (error) {
                setErrors(prev => ({ ...prev, userId: error }));
            }
        }
    };

    const validatePassword = (password: string) => {
        if (!password) return 'Required';
        
        const errors: string[] = [];
        
        if (password.length < 8) {
          errors.push(`min 8 chars (${password.length}/8)`);
        }
        if (!/[A-Z]/.test(password)) {
          errors.push('uppercase letter');
        }
        if (!/[a-z]/.test(password)) {
          errors.push('lowercase letter');
        }
        if (!/\d/.test(password)) {
          errors.push('number');
        }
        
        if (errors.length > 0) {
          return `Missing: ${errors.join(', ')}`;
        }
        return '';
    };

    // Forgot Password Handlers
    const handleForgotPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        if (name === 'mobileNumber' && value.length > 10) return;
        if (name === 'otp' && (value.length > 6 || !/^\d*$/.test(value))) return;

        setForgotPasswordData(prev => ({ ...prev, [name]: value }));

        // Clear existing error
        if (forgotErrors[name]) {
            setForgotErrors(prev => ({ ...prev, [name]: '' }));
        }

        // Validate mobile number
        if (name === 'mobileNumber') {
            const error = validateMobileNumber(value);
            if (error) {
                setForgotErrors(prev => ({ ...prev, mobileNumber: error }));
            }
        }

        // Validate userId
        if (name === 'userId') {
            const error = validateUserId(value);
            if (error) {
                setForgotErrors(prev => ({ ...prev, userId: error }));
            }
        }

        // Validate new password
        if (name === 'newPassword') {
            const error = validatePassword(value);
            if (error) {
                setForgotErrors(prev => ({ ...prev, newPassword: error }));
            }
            // Also check confirm password match if it has a value
            if (forgotPasswordData.confirmPassword && value !== forgotPasswordData.confirmPassword) {
                setForgotErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
            } else if (forgotPasswordData.confirmPassword) {
                setForgotErrors(prev => ({ ...prev, confirmPassword: '' }));
            }
        }

        // Validate confirm password
        if (name === 'confirmPassword') {
            if (value !== forgotPasswordData.newPassword) {
                setForgotErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
            }
        }
    };

    const handleSendForgotPasswordOtp = async () => {
        // Validate based on active tab
        if (forgotPasswordTab === 'mobile') {
            const mobileError = validateMobileNumber(forgotPasswordData.mobileNumber);
            if (mobileError) {
                setForgotErrors(prev => ({ ...prev, mobileNumber: mobileError }));
                return;
            }
        } else {
            const userIdError = validateUserId(forgotPasswordData.userId);
            if (userIdError) {
                setForgotErrors(prev => ({ ...prev, userId: userIdError }));
                return;
            }
        }

        setForgotOtpLoading(true);

        try {
            const response = await API.post(`/auth/send-otp`, {
                userId: forgotPasswordData.userId,
                mobileNumber: forgotPasswordData.mobileNumber,
                countryCode: forgotPasswordData.countryCode
            });

            const data = response.data;
            if (data.status !== 'RS_OK') {
                toast.error(data.errorDescription || 'Failed to send OTP');
                setForgotErrors(prev => ({ ...prev, form: data.errorDescription || 'Failed to send OTP' }));
            } else {
                toast.success('OTP sent successfully');
                setForgotPasswordData(prev => ({ ...prev, token: data.token || '' }));
                setForgotOtpSent(true);
                setForgotOtpTimer(90);
                setForgotErrors({}); // Clear any previous errors
            }
        } catch (error) {
            console.error('Failed to send forgot password OTP:', error);
            setForgotErrors(prev => ({ ...prev, form: 'An error occurred while sending OTP' }));
        } finally {
            setForgotOtpLoading(false);
        }
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate OTP (must be exactly 6 digits)
        if (!forgotPasswordData.otp || forgotPasswordData.otp.length !== 6) {
            setForgotErrors(prev => ({ ...prev, otp: 'Please enter a valid 6-digit OTP' }));
            return;
        }

        // Validate new password using validatePassword function
        const passwordError = validatePassword(forgotPasswordData.newPassword);
        if (passwordError) {
            setForgotErrors(prev => ({ ...prev, newPassword: passwordError }));
            return;
        }

        // Validate confirm password
        if (forgotPasswordData.newPassword !== forgotPasswordData.confirmPassword) {
            setForgotErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
            return;
        }

        setForgotResetLoading(true);

        try {
            const response = await API.post(`/auth/forgot-password`, {
                userId: forgotPasswordData.userId,
                mobileNumber: forgotPasswordData.mobileNumber,
                countryCode: forgotPasswordData.countryCode,
                token: forgotPasswordData.token,
                otp: forgotPasswordData.otp,
                password: forgotPasswordData.newPassword
            });

            const data = response.data;
            if (data.status !== 'RS_OK') {
                toast.error(data.errorDescription || 'Failed to reset password');
                setForgotErrors(prev => ({ ...prev, form: data.errorDescription || 'Failed to reset password' }));
            } else {
                toast.success('Password reset successfully! Please login with your new password.');
                // Reset forgot password state and go back to login
                setShowForgotPassword(false);
                setForgotOtpSent(false);
                setForgotPasswordData({
                    userId: '',
                    mobileNumber: '',
                    countryCode: '91',
                    otp: '',
                    newPassword: '',
                    confirmPassword: '',
                    token: ''
                });
                setForgotErrors({});
            }
        } catch (error) {
            console.error('Failed to reset password:', error);
            setForgotErrors(prev => ({ ...prev, form: 'An error occurred while resetting password' }));
        } finally {
            setForgotResetLoading(false);
        }
    };

    const handleBackFromForgotPassword = () => {
        setShowForgotPassword(false);
        setForgotOtpSent(false);
        setForgotOtpTimer(0);
        setForgotPasswordData({
            userId: '',
            mobileNumber: '',
            countryCode: '91',
            otp: '',
            newPassword: '',
            confirmPassword: '',
            token: ''
        });
        setForgotErrors({});
    };

    const handleResendForgotPasswordOtp = async () => {
        if (forgotOtpTimer > 0 || forgotOtpLoading) return;
        
        setForgotOtpLoading(true);
        try {
            const response = await API.post(`/auth/send-otp`, {
                userId: forgotPasswordData.userId,
                mobileNumber: forgotPasswordData.mobileNumber,
                countryCode: forgotPasswordData.countryCode
            });

            const data = response.data;
            if (data.status !== 'RS_OK') {
                toast.error(data.errorDescription || 'Failed to resend OTP');
            } else {
                toast.success('OTP resent successfully');
                setForgotPasswordData(prev => ({ ...prev, token: data.token || '', otp: '' }));
                setForgotOtpTimer(90);
            }
        } catch (error) {
            console.error('Failed to resend forgot password OTP:', error);
            toast.error('An error occurred while resending OTP');
        } finally {
            setForgotOtpLoading(false);
        }
    };

    const handleDemoLogin = async () => {
        if (demoLoading) return;
        setDemoLoading(true);
        try {
            const response = await TOKEN_API.post(`/api/v1/auth/demo-login`, {
            });

            const data = response.data;
            if (data?.status !== 'RS_OK') {
                toast.error(data?.errorDescription || 'Demo login failed');
                return;
            }

            if (data?.userStatus === 'BLOCKED') {
                sessionStorage.setItem('user_blocked', 'true');
            }

            if (data?.userKey) sessionStorage.setItem('userKey', data.userKey);
            if (data?.accessToken) sessionStorage.setItem('token', data.accessToken);
            if (data?.refreshToken) sessionStorage.setItem('rtoken', data.refreshToken);
            sessionStorage.setItem('isDemo', 'true');
            sessionStorage.removeItem('lowBalancePopupClosed'); // Reset popup flag on login
            sessionStorage.removeItem("emptyUserIdAlertDismissed");

            dispatch(setIsLoggedIn(true));
            dispatch(setBrowser('Web'));
            toast.success('Demo login successful');
            navigate('/home');
        } catch (err) {
            console.error('Demo login error', err);
            toast.error('Unable to complete demo login');
        } finally {
            setDemoLoading(false);
        }
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();

        // if (!showUserIdLogin) {
        //     setActiveTab('mobile');
        // }

        // Validate based on active tab
        if (activeTab === 'mobile') {
            const mobileError = validateMobileNumber(userData.mobileNumber);
            if (mobileError) {
                setErrors(prev => ({ ...prev, mobileNumber: mobileError }));
                return;
            }
        } else if (activeTab === 'userId') {
            const userIdError = validateUserId(userData.userId || '');
            if (userIdError) {
                setErrors(prev => ({ ...prev, userId: userIdError }));
                return;
            }
        }

        if (!userData.password) {
            setErrors(prev => ({ ...prev, password: t('auth.messages.passwordIsRequired') }));
            return;
        }

        setLoading(true);

        try {
            const loginPayload: any = {
                groupBotId: "1844",
                password: userData.password,
            };

            // Add login method based on active tab
            if (activeTab === 'mobile') {
                loginPayload.mobileNumber = userData.mobileNumber;
                loginPayload.countryCode = userData.countryCode;
            } else if (activeTab === 'userId') {
                loginPayload.userId = userData.userId;
            }

            const response = await API.post(`/auth/login`, loginPayload);

            const data = await response.data;
            if (data.status !== "RS_OK") {
                toast.error(`${data.errorDescription}`);
            } else {
                if (data.status === "RS_OK" && data.userStatus === "BLOCKED") {
                    sessionStorage.setItem("user_blocked", "true");
                }
                sessionStorage.setItem("userKey", response?.data?.userKey)
                sessionStorage.setItem("token", response?.data?.accessToken);
                sessionStorage.setItem("rtoken", response?.data?.refreshToken);
                sessionStorage.setItem("isDemo", "false");
                sessionStorage.removeItem('lowBalancePopupClosed'); // Reset popup flag on login
                sessionStorage.removeItem("emptyUserIdAlertDismissed");
                dispatch(setIsLoggedIn(true));
                dispatch(setBrowser("Web"));
                toast.success(t('auth.messages.loggedInSuccessfully'));
                navigate("/home")
            }
        } catch (error) {
            console.error("Login failed:", error);
            setErrors(prev => ({ ...prev, form: t('auth.messages.anErrorOccurredDuringLogin') }));
        } finally {
            setLoading(false);
        }
    };

    const handleSendOtp = async (e: React.FormEvent) => {
        e.preventDefault();

        const mobileError = validateMobileNumber(userData.mobileNumber);
        if (mobileError) {
            setErrors(prev => ({ ...prev, mobileNumber: mobileError }));
            return;
        }

        setOtpLoading(true);

        try {
            const response = await API.post(`/auth/send-otp`, {
                mobileNumber: userData.mobileNumber,
                countryCode: "91",
                groupBotId: "1844"
            });

            const data = await response.data;
            if (data.status !== "RS_OK") {
                toast.error(`${data.errorDescription}`);
                setOtpLoading(false);
                setErrors(prev => ({ ...prev, form: data.errorDescription || t('auth.messages.failedToSendOtp') }));
            } else {
                toast.success(t('auth.messages.otpSentSuccessfully'));
                updateUserData({ otpToken: response.data.token, otpMessage: data.message || '' });
                startOtpTimer();
                goToNextStep();
            }
        } catch (error) {
            console.error("Failed to send OTP:", error);
            setErrors(prev => ({ ...prev, form: t('auth.messages.anErrorOccurredWhileSendingOtp') }));
        } finally {
            setOtpLoading(false);
        }
    };

    const handleCheckUser = async (e: React.FormEvent) => {
        e.preventDefault();
        const mobileError = validateMobileNumber(userData.mobileNumber);
        if (mobileError) {
            setErrors(prev => ({ ...prev, mobileNumber: mobileError }));
            return;
        }
        setLoading(true);
        try {
            // Replace with your actual API endpoint for checking user existence
            const response = await API.post('/auth/is-existing-user', {
                mobileNumber: userData.mobileNumber,
                countryCode: userData.countryCode,
                isRegister: false
            });
            const data = await response.data;
            if (data.status !== 'RS_OK') {
                toast.error(data.errorDescription || t('auth.messages.failedToCheckUser'));
                setErrors(prev => ({ ...prev, form: data.errorDescription || t('auth.messages.failedToCheckUser') }));
            } else {

                if (data.isExistingUser && data.isPasswordSet) {
                    setIsExistingUser(true);
                } else {
                    updateUserData({ otpToken: data.token, otpMessage: data.message || '' });
                    startOtpTimer();
                    goToNextStep();

                }
            }
        } catch (error) {
            console.error('User check failed:', error);
            setErrors(prev => ({ ...prev, form: t('auth.messages.anErrorOccurredWhileCheckingUser') }));
        } finally {
            setLoading(false);
        }
    };

    // For BidExch and hindu777 brands, show tab-based login
    // if (showUserIdLogin) {
    //     return (
    //         <div className="flex flex-col w-full">
    //             {/* Tab Navigation */}
    //             <div className="flex mb-4 bg-zinc-700 rounded-md p-1">
    //                 <button
    //                     type="button"
    //                     onClick={() => setActiveTab('userId')}
    //                     className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
    //                         activeTab === 'userId'
    //                             ? 'bg-blue-600 text-white'
    //                             : 'text-zinc-300 hover:text-white hover:bg-zinc-600'
    //                     }`}
    //                 >
    //                     User ID
    //                 </button>
    //                 <button
    //                     type="button"
    //                     onClick={() => setActiveTab('mobile')}
    //                     className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
    //                         activeTab === 'mobile'
    //                             ? 'bg-blue-600 text-white'
    //                             : 'text-zinc-300 hover:text-white hover:bg-zinc-600'
    //                     }`}
    //                 >
    //                     Mobile Number
    //                 </button>
    //             </div>

    //             {/* Tab Content */}
    //             {activeTab === 'userId' && (
    //                 <form className="flex flex-col w-full" onSubmit={handleLogin}>
    //                     <label className="text-sm text-zinc-400 mb-1">
    //                         User ID <span className="text-red-500">*</span>
    //                     </label>
    //                     <input
    //                         type="text"
    //                         name="userId"
    //                         placeholder="Enter your User ID"
    //                         value={userData.userId || ''}
    //                         onChange={handleChange}
    //                         className={`p-2 w-full border rounded-md text-sm bg-zinc-800 text-white border-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.userId ? 'border-red-500' : ''}`}
    //                     />
    //                     {errors.userId && (
    //                         <span className="text-red-500 text-xs mt-1">{errors.userId}</span>
    //                     )}

    //                     <label className="text-sm text-zinc-400 mb-1 mt-4">
    //                         Password <span className="text-red-500">*</span>
    //                     </label>
    //                     <div className="relative w-full">
    //                         <input
    //                             type={showPassword ? 'text' : 'password'}
    //                             name="password"
    //                             placeholder="Password"
    //                             value={userData.password}
    //                             onChange={handleChange}
    //                             className={`p-2 pr-10 w-full border rounded-md text-sm bg-zinc-800 text-white border-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.password ? 'border-red-500' : ''}`}
    //                         />
    //                         <button
    //                             type="button"
    //                             onClick={() => setShowPassword((prev) => !prev)}
    //                             className="absolute inset-y-0 right-2 flex items-center text-white"
    //                         >
    //                             {showPassword ? (
    //                                 <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    //                                     <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
    //                                     <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
    //                                 </svg>
    //                             ) : (
    //                                 <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    //                                     <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
    //                                     <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
    //                                 </svg>
    //                             )}
    //                         </button>
    //                     </div>
    //                     {errors.password && (
    //                         <span className="text-red-500 text-xs mt-1">{errors.password}</span>
    //                     )}

    //                     {errors.form && (
    //                         <div className="bg-red-900/30 border border-red-500 p-2 rounded-md mb-4 mt-4">
    //                             <p className="text-red-200 text-xs">{errors.form}</p>
    //                         </div>
    //                     )}

    //                     <button
    //                         type="submit"
    //                         className="p-2.5 mt-4 w-full text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
    //                         disabled={loading || !userData.userId || !userData.password}
    //                     >
    //                         {loading ? (
    //                             <>
    //                                 <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    //                                     <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    //                                     <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    //                                 </svg>
    //                                 Logging in...
    //                             </>
    //                         ) : (
    //                             'LOGIN'
    //                         )}
    //                     </button>
    //                 </form>
    //             )}

    //             {activeTab === 'mobile' && (
    //                 <>
    //                     {!isExistingUser && (
    //                         <form className="flex flex-col w-full" onSubmit={handleCheckUser}>
    //                             <label className="text-sm text-zinc-400 mb-1">
    //                                 Mobile Number <span className="text-red-500">*</span>
    //                             </label>
    //                             <div className="flex items-center">
    //                                 <span className="px-3 py-2 text-sm bg-zinc-700 text-white border border-r-0 border-zinc-700 rounded-l-md">
    //                                     +{userData.countryCode}
    //                                 </span>
    //                                 <input
    //                                     type="number"
    //                                     name="mobileNumber"
    //                                     placeholder="Mobile number"
    //                                     value={userData.mobileNumber}
    //                                     onChange={handleChange}
    //                                     className={`p-2 w-full border rounded-r-md text-sm bg-zinc-800 text-white border-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.mobileNumber ? 'border-red-500' : ''}`}
    //                                 />
    //                             </div>
    //                             {errors.mobileNumber && (
    //                                 <span className="text-red-500 text-xs mt-1">{errors.mobileNumber}</span>
    //                             )}

    //                             <button
    //                                 type="submit"
    //                                 className="p-2.5 mt-4 w-full text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
    //                                 disabled={loading || userData.mobileNumber.length !== 10}
    //                             >
    //                                 {loading ? (
    //                                     <>
    //                                         <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    //                                             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    //                                             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    //                                         </svg>
    //                                         Checking...
    //                                     </>
    //                                 ) : (
    //                                     'Login'
    //                                 )}
    //                             </button>
    //                         </form>
    //                     )}

    //                     {isExistingUser && (
    //                         <form className="flex flex-col w-full" onSubmit={handleLogin}>
    //                             <div className="flex items-center justify-between mb-4">
    //                                 <label className="text-sm text-zinc-400">
    //                                     Mobile Number
    //                                 </label>
    //                                 <div className="flex items-center">
    //                                     <span className="text-sm text-white">+{userData.countryCode} {userData.mobileNumber}</span>
    //                                     <button
    //                                         type="button"
    //                                         onClick={() => setIsExistingUser(false)}
    //                                         className="ml-2 text-blue-400 text-sm hover:underline"
    //                                     >
    //                                         Edit
    //                                     </button>
    //                                 </div>
    //                             </div>

    //                             <label className="text-sm text-zinc-400 mb-1">
    //                                 Password <span className="text-red-500">*</span>
    //                             </label>
    //                             <div className="relative w-full">
    //                                 <input
    //                                     type={showPassword ? 'text' : 'password'}
    //                                     name="password"
    //                                     placeholder="Password"
    //                                     value={userData.password}
    //                                     onChange={handleChange}
    //                                     className={`p-2 pr-10 w-full border rounded-md text-sm bg-zinc-800 text-white border-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.password ? 'border-red-500' : ''}`}
    //                                 />
    //                                 <button
    //                                     type="button"
    //                                     onClick={() => setShowPassword((prev) => !prev)}
    //                                     className="absolute inset-y-0 right-2 flex items-center text-white"
    //                                 >
    //                                     {showPassword ? (
    //                                         <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    //                                             <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
    //                                             <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
    //                                         </svg>
    //                                     ) : (
    //                                         <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    //                                             <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
    //                                             <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
    //                                         </svg>
    //                                     )}
    //                                 </button>
    //                             </div>
    //                             {errors.password && (
    //                                 <span className="text-red-500 text-xs mt-1">{errors.password}</span>
    //                             )}

    //                             <div className="text-right mt-2 mb-4">
    //                                 <button
    //                                     type="button"
    //                                     onClick={handleSendOtp}
    //                                     className={`text-sm ${userData.mobileNumber.length === 10
    //                                         ? 'text-blue-400 hover:underline'
    //                                         : 'text-zinc-500 cursor-not-allowed'
    //                                         }`}
    //                                     disabled={userData.mobileNumber.length !== 10 || otpLoading}
    //                                 >
    //                                     {otpLoading ? 'Sending OTP...' : 'Continue with OTP'}
    //                                 </button>
    //                             </div>

    //                             {errors.form && (
    //                                 <div className="bg-red-900/30 border border-red-500 p-2 rounded-md mb-4">
    //                                     <p className="text-red-200 text-xs">{errors.form}</p>
    //                                 </div>
    //                             )}

    //                             <button
    //                                 type="submit"
    //                                 className="p-2.5 w-full text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
    //                                 disabled={loading || userData.mobileNumber.length !== 10 || !userData.password}
    //                             >
    //                                 {loading ? (
    //                                     <>
    //                                         <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    //                                             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    //                                             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    //                                         </svg>
    //                                         Logging in...
    //                                     </>
    //                                 ) : (
    //                                     'LOGIN'
    //                                 )}
    //                             </button>
    //                         </form>
    //                     )}
    //                 </>
    //             )}
    //         </div>
    //     );
    // }

    // Forgot Password Form
    if (showForgotPassword) {
        return (
            <div className="flex flex-col w-full">
                {/* Back Button */}
                <button
                    type="button"
                    onClick={handleBackFromForgotPassword}
                    className={`flex items-center ${linkClass} hover:opacity-80 mb-4 text-sm`}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back
                </button>

                {/* Title */}
                <h2 className={`text-xl font-bold text-center mb-2 ${pageTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: pageTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>Forgot UserId/Password?</h2>
                <p className={`text-sm text-center mb-6 ${pageTextColorClass === 'dark-text' ? 'text-gray-700' : 'text-zinc-400'}`} style={{ color: pageTextColorClass === 'dark-text' ? '#374151' : '#A1A1AA' }}>
                    We'll send OTP on your registered number associated with userId.
                </p>

                <form onSubmit={handleResetPassword} className="flex flex-col w-full">
                    {/* Tab Selection for User ID / Mobile */}
                    <div className={`flex mb-4 ${tabBgClass} ${tabRoundedClass} p-1`} style={backgroundTeamInfo ? { backgroundColor: backgroundTeamInfo } : {}}>
                        <button
                            type="button"
                            onClick={() => {
                                setForgotPasswordTab('userId');
                                setForgotPasswordData(prev => ({ ...prev, mobileNumber: '' }));
                                setForgotErrors(prev => ({ ...prev, mobileNumber: '' }));
                            }}
                            className={`flex-1 py-2 px-3 text-sm font-medium ${tabRoundedClass} transition-colors ${forgotPasswordTab === 'userId'
                                ? tabActiveClass
                                : tabInactiveClass
                                }`}
                        >
                            UserId
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setForgotPasswordTab('mobile');
                                setForgotPasswordData(prev => ({ ...prev, userId: '' }));
                                setForgotErrors(prev => ({ ...prev, userId: '' }));
                            }}
                            className={`flex-1 py-2 px-3 text-sm font-medium ${tabRoundedClass} transition-colors ${forgotPasswordTab === 'mobile'
                                ? tabActiveClass
                                : tabInactiveClass
                                }`}
                        >
                            Mobile Number
                        </button>
                    </div>

                    {/* User ID / Mobile Input */}
                    {forgotPasswordTab === 'userId' ? (
                        <div className="mb-4">
                            <label className={`text-sm mb-1 block ${inputLabelTextColorClass} ${inputLabelTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: inputLabelTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>
                                UserId <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="userId"
                                placeholder="Enter Your UserId"
                                value={forgotPasswordData.userId}
                                onChange={handleForgotPasswordChange}
                                disabled={forgotOtpSent}
                                className={`p-2 w-full border rounded-md text-sm ${inputClass} focus:outline-none focus:ring-2 disabled:opacity-60 ${forgotErrors.userId ? 'border-red-500' : ''}`}
                                style={{ 
                                    ...(inputBackgroundBox ? { backgroundColor: inputBackgroundBox } : {}),
                                    ...(tableBorder && !forgotErrors.userId ? { borderColor: tableBorder } : {})
                                }}
                            />
                            {forgotErrors.userId && (
                                <span className="text-red-500 text-xs mt-1">{forgotErrors.userId}</span>
                            )}
                        </div>
                    ) : (
                        <div className="mb-4">
                            <label className={`text-sm mb-1 block ${inputLabelTextColorClass} ${inputLabelTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: inputLabelTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>
                                Mobile Number <span className="text-red-500">*</span>
                            </label>
                            <div className="flex items-center">
                                <span 
                                    className={`px-3 py-2 text-sm ${countryCodeClass} border border-r-0 rounded-l-md`}
                                    style={{
                                        ...countryCodeBorderStyle,
                                        ...(inputBackgroundBox ? { backgroundColor: inputBackgroundBox } : {}),
                                        color: countryCodeTextColorClass === 'dark-text' ? '#000000' : (isLovable ? undefined : '#FFFFFF')
                                    }}
                                >
                                    +{forgotPasswordData.countryCode}
                                </span>
                                <input
                                    type="number"
                                    name="mobileNumber"
                                    placeholder="Enter Mobile Number"
                                    value={forgotPasswordData.mobileNumber}
                                    onChange={handleForgotPasswordChange}
                                    disabled={forgotOtpSent}
                                    className={`p-2 w-full border rounded-r-md text-sm ${inputClass} focus:outline-none focus:ring-2 disabled:opacity-60 ${forgotErrors.mobileNumber ? 'border-red-500' : ''}`}
                                    style={{ 
                                        ...(inputBackgroundBox ? { backgroundColor: inputBackgroundBox } : {}),
                                        ...(tableBorder && !forgotErrors.mobileNumber ? { borderColor: tableBorder } : {})
                                    }}
                                />
                            </div>
                            {forgotErrors.mobileNumber && (
                                <span className="text-red-500 text-xs mt-1">{forgotErrors.mobileNumber}</span>
                            )}
                        </div>
                    )}

                    {/* Error Message (before OTP sent) */}
                    {!forgotOtpSent && forgotErrors.form && (
                        <div className="bg-red-900/30 border border-red-500 p-2 rounded-md mb-4">
                            <p className="text-red-200 text-xs">{forgotErrors.form}</p>
                        </div>
                    )}

                    {/* Send OTP Button */}
                    {!forgotOtpSent && (
                        <button
                            type="button"
                            onClick={handleSendForgotPasswordOtp}
                            disabled={forgotOtpLoading || (forgotPasswordTab === 'userId' ? !forgotPasswordData.userId : forgotPasswordData.mobileNumber.length !== 10)}
                            className={`p-2.5 mb-4 w-full text-sm ${primaryBtnClass} rounded-md transition-colors disabled:opacity-50 flex items-center justify-center ${primaryColor ? (activeTabTextColorClass === 'dark-text' ? 'text-black' : 'text-white') : ''}`}
                            style={primaryColor ? { 
                                backgroundColor: primaryColor, 
                                color: getTextColorForPrimaryColor(primaryColor) || (activeTabTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF')
                            } : {}}
                        >
                            {forgotOtpLoading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Sending OTP...
                                </>
                            ) : (
                                'Send OTP'
                            )}
                        </button>
                    )}

                    {/* OTP Sent - Show remaining fields */}
                    {forgotOtpSent && (
                        <>
                            {/* OTP Input */}
                            <div className="mb-4">
                                <label className={`text-sm mb-1 block ${inputLabelTextColorClass === 'dark-text' ? 'text-gray-700' : 'text-zinc-400'}`} style={{ color: inputLabelTextColorClass === 'dark-text' ? '#374151' : '#A1A1AA' }}>
                                    OTP <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="otp"
                                    placeholder="Enter 6-digit OTP"
                                    value={forgotPasswordData.otp}
                                    onChange={handleForgotPasswordChange}
                                    maxLength={6}
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    className={`p-2 w-full border rounded-md text-sm ${inputClass} focus:outline-none focus:ring-2 ${forgotErrors.otp ? 'border-red-500' : ''}`}
                                    style={{ 
                                        ...(inputBackgroundBox ? { backgroundColor: inputBackgroundBox } : {}),
                                        ...(tableBorder && !forgotErrors.otp ? { borderColor: tableBorder } : {})
                                    }}
                                />
                                {forgotErrors.otp && (
                                    <span className="text-red-500 text-xs mt-1">{forgotErrors.otp}</span>
                                )}
                                {/* Timer and Resend OTP */}
                                <div className="flex items-center justify-between mt-2">
                                    {forgotOtpTimer > 0 ? (
                                        <span className={`text-xs ${inputLabelTextColorClass === 'dark-text' ? 'text-gray-700' : 'text-zinc-400'}`} style={{ color: inputLabelTextColorClass === 'dark-text' ? '#374151' : '#A1A1AA' }}>
                                            Resend OTP in <span className={`${timerHighlightClass} font-medium`}>{forgotOtpTimer}s</span>
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={handleResendForgotPasswordOtp}
                                            disabled={forgotOtpLoading}
                                            className={`${timerHighlightClass} text-xs hover:underline disabled:opacity-50`}
                                        >
                                            {forgotOtpLoading ? 'Sending...' : 'Resend OTP'}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* New Password Input */}
                            <div className="mb-4">
                                <label className={`text-sm mb-1 block ${inputLabelTextColorClass === 'dark-text' ? 'text-gray-700' : 'text-zinc-400'}`} style={{ color: inputLabelTextColorClass === 'dark-text' ? '#374151' : '#A1A1AA' }}>
                                    New Password <span className="text-red-500">*</span>
                                </label>
                                <div className="relative w-full">
                                    <input
                                        type={showNewPassword ? 'text' : 'password'}
                                        name="newPassword"
                                        placeholder="Enter New Password"
                                        value={forgotPasswordData.newPassword}
                                        onChange={handleForgotPasswordChange}
                                        className={`p-2 pr-10 w-full border rounded-md text-sm ${inputClass} focus:outline-none focus:ring-2 ${forgotErrors.newPassword ? 'border-red-500' : ''}`}
                                        style={{ 
                                            ...(inputBackgroundBox ? { backgroundColor: inputBackgroundBox } : {}),
                                            ...(tableBorder && !forgotErrors.newPassword ? { borderColor: tableBorder } : {})
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewPassword(prev => !prev)}
                                        className={`absolute inset-y-0 right-2 flex items-center ${iconClass}`}
                                        style={{ color: iconTextColorClass === 'dark-text' ? '#000000' : (isLovable ? undefined : '#FFFFFF') }}
                                    >
                                        {showNewPassword ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                <circle cx="12" cy="12" r="3" />
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                                <line x1="1" y1="1" x2="23" y2="23" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                                {forgotErrors.newPassword && (
                                    <span className="text-red-500 text-xs mt-1">{forgotErrors.newPassword}</span>
                                )}
                            </div>

                            {/* Confirm Password Input */}
                            <div className="mb-4">
                                <label className={`text-sm mb-1 block ${inputLabelTextColorClass === 'dark-text' ? 'text-gray-700' : 'text-zinc-400'}`} style={{ color: inputLabelTextColorClass === 'dark-text' ? '#374151' : '#A1A1AA' }}>
                                    Confirm Password <span className="text-red-500">*</span>
                                </label>
                                <div className="relative w-full">
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        name="confirmPassword"
                                        placeholder="Enter Confirm Password"
                                        value={forgotPasswordData.confirmPassword}
                                        onChange={handleForgotPasswordChange}
                                        className={`p-2 pr-10 w-full border rounded-md text-sm ${inputClass} focus:outline-none focus:ring-2 ${forgotErrors.confirmPassword ? 'border-red-500' : ''}`}
                                        style={{ 
                                            ...(inputBackgroundBox ? { backgroundColor: inputBackgroundBox } : {}),
                                            ...(tableBorder && !forgotErrors.confirmPassword ? { borderColor: tableBorder } : {})
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(prev => !prev)}
                                        className={`absolute inset-y-0 right-2 flex items-center ${iconClass}`}
                                        style={{ color: iconTextColorClass === 'dark-text' ? '#000000' : (isLovable ? undefined : '#FFFFFF') }}
                                    >
                                        {showConfirmPassword ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                <circle cx="12" cy="12" r="3" />
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                                <line x1="1" y1="1" x2="23" y2="23" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                                {forgotErrors.confirmPassword && (
                                    <span className="text-red-500 text-xs mt-1">{forgotErrors.confirmPassword}</span>
                                )}
                            </div>

                            {/* Error Message */}
                            {forgotErrors.form && (
                                <div className="bg-red-900/30 border border-red-500 p-2 rounded-md mb-4">
                                    <p className="text-red-200 text-xs">{forgotErrors.form}</p>
                                </div>
                            )}

                            {/* Reset Password Button */}
                            <button
                                type="submit"
                                disabled={
                                    forgotResetLoading || 
                                    forgotPasswordData.otp.length !== 6 || 
                                    !!validatePassword(forgotPasswordData.newPassword) || 
                                    !forgotPasswordData.confirmPassword || 
                                    forgotPasswordData.newPassword !== forgotPasswordData.confirmPassword
                                }
                                className={`p-2.5 w-full text-sm ${secondaryBtnClass} rounded-md transition-colors disabled:opacity-50 flex items-center justify-center`}
                            >
                                {forgotResetLoading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Resetting...
                                    </>
                                ) : (
                                    'Reset Password'
                                )}
                            </button>
                        </>
                    )}
                </form>

                {/* Sign Up Link */}
                <div className="text-center mt-4">
                    <span className={`text-sm ${pageTextColorClass === 'dark-text' ? 'text-gray-700' : 'text-zinc-400'}`} style={{ color: pageTextColorClass === 'dark-text' ? '#374151' : '#A1A1AA' }}>Don't have account? </span>
                    <button
                        type="button"
                        onClick={handleRegisterClick}
                        className={`${linkClass} text-sm hover:underline`}
                    >
                        Sign Up
                    </button>
                </div>
            </div>
        );
    }

    // For other brands, show the original mobile-only login
    return (
        <>
            <div className="flex flex-col w-full">
                {/* Tab Navigation */}
                <div className={`flex mb-4 ${tabBgClass} ${tabRoundedClass} p-1`} style={backgroundTeamInfo ? { backgroundColor: backgroundTeamInfo } : {}}>
                    <button
                        type="button"
                        onClick={() => setActiveTab('userId')}
                        className={`flex-1 py-2 px-3 text-sm font-medium ${tabRoundedClass} transition-colors ${activeTab === 'userId' && !primaryColor ? tabActiveClass : ''} ${activeTab !== 'userId' ? tabInactiveClass : ''} ${activeTab === 'userId' && primaryColor ? (activeTabTextColorClass === 'dark-text' ? 'text-black' : 'text-white') : ''} ${activeTab !== 'userId' ? (inactiveTabTextColorClass === 'dark-text' ? 'text-black' : 'text-white') : ''}`}
                        style={activeTab === 'userId' && primaryColor ? { 
                            backgroundColor: primaryColor, 
                            color: getTextColorForPrimaryColor(primaryColor) || (activeTabTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF')
                        } : activeTab !== 'userId' ? {
                            color: inactiveTabTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF'
                        } : {}}
                    >
                        User ID
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('mobile')}
                        className={`flex-1 py-2 px-3 text-sm font-medium ${tabRoundedClass} transition-colors ${activeTab === 'mobile' && !primaryColor ? tabActiveClass : ''} ${activeTab !== 'mobile' ? tabInactiveClass : ''} ${activeTab === 'mobile' && primaryColor ? (activeTabTextColorClass === 'dark-text' ? 'text-black' : 'text-white') : ''} ${activeTab !== 'mobile' ? (inactiveTabTextColorClass === 'dark-text' ? 'text-black' : 'text-white') : ''}`}
                        style={activeTab === 'mobile' && primaryColor ? { 
                            backgroundColor: primaryColor, 
                            color: getTextColorForPrimaryColor(primaryColor) || (activeTabTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF')
                        } : activeTab !== 'mobile' ? {
                            color: inactiveTabTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF'
                        } : {}}
                    >
                        Mobile Number
                    </button>
                </div>

                {/* Tab Content */}
                {activeTab === 'userId' && (
                    <form className="flex flex-col w-full" onSubmit={handleLogin}>
                        <label className={`text-sm mb-1 ${labelTextIsDark ? 'text-black' : 'text-white'}`} style={{ color: labelTextColorValue }}>
                            User ID <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="userId"
                            placeholder="Enter your User ID"
                            value={userData.userId || ''}
                            onChange={handleChange}
                            className={`p-2 w-full border rounded-md text-sm ${inputClass} focus:outline-none focus:ring-2 ${errors.userId ? 'border-red-500' : ''}`}
                            style={{ 
                                ...(inputBackgroundBox ? { backgroundColor: inputBackgroundBox } : {}),
                                ...(tableBorder && !errors.userId ? { borderColor: tableBorder } : {})
                            }}
                        />
                        {errors.userId && (
                            <span className="text-red-500 text-xs mt-1">{errors.userId}</span>
                        )}

                        <label className={`text-sm mb-1 mt-4 ${labelTextIsDark ? 'text-black' : 'text-white'}`} style={{ color: labelTextColorValue }}>
                            Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative w-full">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                placeholder="Enter your Password"
                                value={userData.password}
                                onChange={handleChange}
                                className={`p-2 pr-10 w-full border rounded-md text-sm ${inputClass} focus:outline-none focus:ring-2 ${errors.password ? 'border-red-500' : ''}`}
                                style={{ 
                                    ...(inputBackgroundBox ? { backgroundColor: inputBackgroundBox } : {}),
                                    ...(tableBorder && !errors.password ? { borderColor: tableBorder } : {})
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword((prev) => !prev)}
                                className={`absolute inset-y-0 right-2 flex items-center ${iconClass}`}
                                style={{ color: iconTextColorClass === 'dark-text' ? '#000000' : (isLovable ? undefined : '#FFFFFF') }}
                            >
                                {showPassword ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                        <line x1="1" y1="1" x2="23" y2="23" />
                                    </svg>
                                )}
                            </button>
                        </div>
                        {errors.password && (
                            <span className="text-red-500 text-xs mt-1">{errors.password}</span>
                        )}

                        {/* Forgot Password Link */}
                        <div className="text-right mt-2">
                            <button
                                type="button"
                                onClick={() => setShowForgotPassword(true)}
                                className={`${linkClass} text-sm hover:underline`}
                            >
                                Forgot Password?
                            </button>
                        </div>

                        {errors.form && (
                            <div className="bg-red-900/30 border border-red-500 p-2 rounded-md mb-4 mt-4">
                                <p className="text-red-200 text-xs">{errors.form}</p>
                            </div>
                        )}

                        <button
                            type="submit"
                            className={`p-2.5 mt-4 w-full text-sm ${primaryBtnClass} rounded-md transition-colors disabled:opacity-50 flex items-center justify-center ${primaryColor ? (activeTabTextColorClass === 'dark-text' ? 'text-black' : 'text-white') : ''}`}
                            style={primaryColor ? { 
                                backgroundColor: primaryColor, 
                                color: getTextColorForPrimaryColor(primaryColor) || (activeTabTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF')
                            } : {}}
                            disabled={loading || !userData.userId || !userData.password}
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Logging In
                                </>
                            ) : (
                                "Login"
                            )}
                        </button>

                        {(() => {
                            const demoFlag = siteConfig?.demoLogin as unknown;
                            const isDemoEnabled = demoFlag === true || (typeof demoFlag === 'string' && demoFlag.toLowerCase() === 'true');
                            return isDemoEnabled;
                        })() && (
                                <button
                                    type="button"
                                    onClick={handleDemoLogin}
                                    disabled={demoLoading}
                                    className="p-2.5 mt-4 w-full text-sm text-white bg-emerald-600 rounded-md hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                                >
                                    {demoLoading ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Demo Login
                                        </>
                                    ) : (
                                        "Demo Login"
                                    )}
                                </button>
                            )}

                        <div className="text-center mt-4">
                            <span className={`text-sm ${pageTextColorClass === 'dark-text' ? 'text-gray-700' : 'text-zinc-400'}`} style={{ color: pageTextColorClass === 'dark-text' ? '#374151' : '#A1A1AA' }}>Don't have an account? </span>
                            <button
                                type="button"
                                onClick={handleRegisterClick}
                                className={`${linkClass} text-sm hover:underline`}
                            >
                                Register
                            </button>
                        </div>
                    </form>
                )}

                {activeTab === 'mobile' && (
                    <>
                        {!isExistingUser && (
                            <form className="flex flex-col w-full" onSubmit={handleCheckUser}>
                                <label className={`text-sm mb-1 ${labelTextIsDark ? 'text-black' : 'text-white'}`} style={{ color: labelTextColorValue }}>
                                    Mobile Number <span className="text-red-500">*</span>
                                </label>
                                <div className="flex items-center">
                                    <span 
                                        className={`px-3 py-2 text-sm ${countryCodeClass} border border-r-0 rounded-l-md`}
                                        style={{
                                            ...countryCodeBorderStyle,
                                            ...(inputBackgroundBox ? { backgroundColor: inputBackgroundBox } : {}),
                                            color: countryCodeTextColorClass === 'dark-text' ? '#000000' : (isLovable ? undefined : '#FFFFFF')
                                        }}
                                    >
                                        +{userData.countryCode}
                                    </span>
                                    <input
                                        type="number"
                                        name="mobileNumber"
                                        placeholder="Enter your Mobile Number"
                                        value={userData.mobileNumber}
                                        onChange={handleChange}
                                        className={`p-2 w-full border rounded-r-md text-sm ${inputClass} focus:outline-none focus:ring-2 ${errors.mobileNumber ? 'border-red-500' : ''}`}
                                        style={{ 
                                            ...(inputBackgroundBox ? { backgroundColor: inputBackgroundBox } : {}),
                                            ...(tableBorder && !errors.mobileNumber ? { borderColor: tableBorder } : {})
                                        }}
                                    />
                                </div>
                                {errors.mobileNumber && (
                                    <span className="text-red-500 text-xs mt-1">{errors.mobileNumber}</span>
                                )}

                                <button
                                    type="submit"
                                    className={`p-2.5 mt-4 w-full text-sm ${primaryBtnClass} rounded-md transition-colors disabled:opacity-50 flex items-center justify-center ${primaryColor ? (activeTabTextColorClass === 'dark-text' ? 'text-black' : 'text-white') : ''}`}
                                    style={primaryColor ? { 
                                        backgroundColor: primaryColor, 
                                        color: getTextColorForPrimaryColor(primaryColor) || (activeTabTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF')
                                    } : {}}
                                    disabled={loading || userData.mobileNumber.length !== 10}
                                >
                                    {loading ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Checking
                                        </>
                                    ) : (
                                        "Login"
                                    )}
                                </button>

                                {(() => {
                                    const demoFlag = siteConfig?.demoLogin as unknown;
                                    const isDemoEnabled = demoFlag === true || (typeof demoFlag === 'string' && demoFlag.toLowerCase() === 'true');
                                    return isDemoEnabled;
                                })() && (
                                        <button
                                            type="button"
                                            onClick={handleDemoLogin}
                                            disabled={demoLoading}
                                            className="p-2.5 mt-4 w-full text-sm text-white bg-emerald-600 rounded-md hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                                        >
                                            {demoLoading ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Demo Login
                                                </>
                                            ) : (
                                                "Demo Login"
                                            )}
                                        </button>
                                    )}

                                <div className="text-center mt-4">
                                    <span className={`text-sm ${pageTextColorClass === 'dark-text' ? 'text-gray-700' : 'text-zinc-400'}`} style={{ color: pageTextColorClass === 'dark-text' ? '#374151' : '#A1A1AA' }}>Don't have an account? </span>
                                    <button
                                        type="button"
                                        onClick={handleRegisterClick}
                                        className={`${linkClass} text-sm hover:underline`}
                                    >
                                        Register
                                    </button>
                                </div>
                            </form>
                        )}

                        {isExistingUser && (
                            <form className="flex flex-col w-full" onSubmit={handleLogin}>
                                <div className="flex items-center justify-between mb-4">
                                    <label className={`text-sm ${labelTextIsDark ? 'text-black' : 'text-white'}`} style={{ color: labelTextColorValue }}>
                                        Mobile Number
                                    </label>
                                    <div className="flex items-center">
                                        <span className={`text-sm ${pageTextColorClass === 'dark-text' ? 'text-black' : 'text-white'}`} style={{ color: pageTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF' }}>+{userData.countryCode} {userData.mobileNumber}</span>
                                        <button
                                            type="button"
                                            onClick={() => setIsExistingUser(false)}
                                            className={`ml-2 ${linkClass} text-sm hover:underline`}
                                        >
                                            Edit
                                        </button>
                                    </div>
                                </div>

                                <label className={`text-sm mb-1 ${labelTextIsDark ? 'text-black' : 'text-white'}`} style={{ color: labelTextColorValue }}>
                                    Password <span className="text-red-500">*</span>
                                </label>
                                <div className="relative w-full">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        name="password"
                                        placeholder="Enter your Password"
                                        value={userData.password}
                                        onChange={handleChange}
                                        className={`p-2 pr-10 w-full border rounded-md text-sm ${inputClass} focus:outline-none focus:ring-2 ${errors.password ? 'border-red-500' : ''}`}
                                        style={{ 
                                            ...(inputBackgroundBox ? { backgroundColor: inputBackgroundBox } : {}),
                                            ...(tableBorder && !errors.password ? { borderColor: tableBorder } : {})
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((prev) => !prev)}
                                        className={`absolute inset-y-0 right-2 flex items-center ${iconClass}`}
                                        style={{ color: iconTextColorClass === 'dark-text' ? '#000000' : (isLovable ? undefined : '#FFFFFF') }}
                                    >
                                        {showPassword ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                <circle cx="12" cy="12" r="3" />
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                                <line x1="1" y1="1" x2="23" y2="23" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                                {errors.password && (
                                    <span className="text-red-500 text-xs mt-1">{errors.password}</span>
                                )}

                                <div className="flex items-center justify-between mt-2 mb-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowForgotPassword(true)}
                                        className={`${linkClass} text-sm hover:underline`}
                                    >
                                        Forgot Password?
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSendOtp}
                                        className={`text-sm ${userData.mobileNumber.length === 10
                                            ? `${linkClass} hover:underline`
                                            : 'text-zinc-500 cursor-not-allowed'
                                            }`}
                                        disabled={userData.mobileNumber.length !== 10 || otpLoading}
                                    >
                                        {otpLoading ? 'Sending OTP' : 'Continue with OTP'}
                                    </button>
                                </div>

                                {errors.form && (
                                    <div className="bg-red-900/30 border border-red-500 p-2 rounded-md mb-4">
                                        <p className="text-red-200 text-xs">{errors.form}</p>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    className={`p-2.5 w-full text-sm ${primaryBtnClass} rounded-md transition-colors disabled:opacity-50 flex items-center justify-center ${primaryColor ? (activeTabTextColorClass === 'dark-text' ? 'text-black' : 'text-white') : ''}`}
                                    style={primaryColor ? { 
                                        backgroundColor: primaryColor, 
                                        color: getTextColorForPrimaryColor(primaryColor) || (activeTabTextColorClass === 'dark-text' ? '#000000' : '#FFFFFF')
                                    } : {}}
                                    disabled={loading || userData.mobileNumber.length !== 10 || !userData.password}
                                >
                                    {loading ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Logging In
                                        </>
                                    ) : (
                                        "Login"
                                    )}
                                </button>

                                {(() => {
                                    const demoFlag = siteConfig?.demoLogin as unknown;
                                    const isDemoEnabled = demoFlag === true || (typeof demoFlag === 'string' && demoFlag.toLowerCase() === 'true');
                                    return isDemoEnabled;
                                })() && (
                                        <button
                                            type="button"
                                            onClick={handleDemoLogin}
                                            disabled={demoLoading}
                                            className="p-2.5 mt-4 w-full text-sm text-white bg-emerald-600 rounded-md hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                                        >
                                            {demoLoading ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Demo Login
                                                </>
                                            ) : (
                                                "Demo Login"
                                            )}
                                        </button>
                                    )}

                                <div className="text-center mt-4">
                                    <span className={`text-sm ${pageTextColorClass === 'dark-text' ? 'text-gray-700' : 'text-zinc-400'}`} style={{ color: pageTextColorClass === 'dark-text' ? '#374151' : '#A1A1AA' }}>Don't have an account? </span>
                                    <button
                                        type="button"
                                        onClick={handleRegisterClick}
                                        className={`${linkClass} text-sm hover:underline`}
                                    >
                                        Register
                                    </button>
                                </div>
                            </form>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default LoginForm;